<template>
    <div class="w-100">

        <!-- Page title -->
        <div class="row align-items-center mt-1 mb-3">
            <div class="col-9">
                <h2 class="page-title">
                    Monitor Wound
                </h2>
            </div>
        </div>

        <!-- Alert box -->
        <div v-show="this.v$.step[this.currentStep - 1].$error" class="alert alert-danger alert-dismissible" role="alert">
            <div class="d-flex">
                <div>
                    <!-- Download SVG icon from http://tabler-icons.io/i/alert-circle -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon alert-icon"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
                        <path d="M12 8v4"></path>
                        <path d="M12 16h.01"></path>
                    </svg>
                </div>
                <div>
                    <span v-if="v$.$errors.length > 1"> Sorry, there are ({{ this.v$.step[this.currentStep - 1].$errors.length }}) errors found please correct the errors to proceed.</span>
                    <span v-if="v$.$errors.length <= 1"> Sorry, there's ({{ this.v$.step[this.currentStep - 1].$errors.length }}) error found please correct the error to proceed.</span>
                </div>
            </div>
            <a class="btn-close" data-bs-dismiss="alert" aria-label="close"></a>
        </div>

        <form id="monitor-wound" action="#" class="add-care-plan add-record">           
            
            <div class="card" v-if="currentStep == 1" title="Observation">
                <div class="card-header">
                    <h3 class="card-title">{{ pageTitle }}</h3> <span class="badge ms-2" :class="(carePlanStatus == 'Active') ? 'bg-green' : ''">{{ carePlanStatus }}</span>
                </div>

                <div class="card-body">

                    <div class="row gx-5">
                        <div class="col-sm-10 col-md-8 col-xl-6"> 
                            <div class="mb-3">
                                <div class="form-label">Size of wound</div>                     
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="sizeOfWound"
                                    placeholder="Size of wound"
                                    v-model="step[0].sizeOfWound"
                                    :class="{'is-invalid': v$.step[0].sizeOfWound.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].sizeOfWound.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>   
                            
                            <div class="mb-3">
                                <div class="form-label">Stage of the wound</div>
                                <select
                                    class="form-select"
                                    name="stageOfTheWound"
                                    v-model="step[0].stageOfTheWound"
                                    :class="{
                                        'is-invalid': v$.step[0].stageOfTheWound.$invalid,
                                    }">
                                    <option value="" disabled>Choose one</option>
                                    <option v-for="option in stageOfTheWoundOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].stageOfTheWound.$invalid}">Invalid feedback</div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Stage of surrounding skin</div>                                
                                <multiselect                                    
                                    name="stageOfSurroundingSkin"
                                    v-model="step[0].stageOfSurroundingSkin"                                   
                                    :class="{'is-invalid': v$.step[0].stageOfSurroundingSkin.$invalid}"

                                    :options="this.stageOfSurroundingSkinOptions" 
                                    :searchable="true" 
                                    :close-on-select="true" 
                                    :show-labels="true" 
                                    :taggable="false"                                    
                                    :multiple="true"

                                    title=""
                                    deselect-label="Deselect"
                                    select-label="Select"
                                    tag-placeholder="Add this as new tag" 
                                    placeholder="Select stage of surrounding skin"

                                ></multiselect>                
                                <div 
                                    class="invalid-feedback"
                                    :class="{'d-block': v$.step[0].stageOfSurroundingSkin.$invalid}"
                                >
                                    Invalid feedback
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="form-label">Exudate</div>
                                <select
                                    class="form-select"
                                    name="exudate"
                                    v-model="step[0].exudate"
                                    :class="{
                                        'is-invalid': v$.step[0].exudate.$invalid,
                                    }">
                                    <option value="" disabled>Choose one</option>
                                    <option v-for="option in exudateOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].exudate.$invalid}">Invalid feedback</div>
                            </div>
                            
                            <div class="mb-3">
                                <div class="form-label">Amount of exudate</div>
                                <select
                                    class="form-select"
                                    name="amountOfExudate"
                                    v-model="step[0].amountOfExudate"
                                    :class="{
                                        'is-invalid': v$.step[0].amountOfExudate.$invalid,
                                    }">
                                    <option value="" disabled>Choose one</option>
                                    <option v-for="option in amountOfExudateOptions" :value="option">
                                        {{ option }}
                                    </option>
                                </select>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].amountOfExudate.$invalid}">Invalid feedback</div>
                            </div>


                        </div>

                        <div class="col-sm-10 col-md-8 col-xl-6">
                            <div class="mb-3">
                                <div class="form-label">Wound Details</div>                                
                                <multiselect                                    
                                    name="woundDetails"
                                    v-model="step[0].woundDetails"                                   
                                    :class="{'is-invalid': v$.step[0].woundDetails.$invalid}"

                                    :options="this.woundDetailsOptions" 
                                    :searchable="true" 
                                    :close-on-select="true" 
                                    :show-labels="true" 
                                    :taggable="false"                                    
                                    :multiple="true"

                                    title=""
                                    deselect-label="Deselect"
                                    select-label="Select"
                                    tag-placeholder="Add this as new tag" 
                                    placeholder="Select wound details"
                                ></multiselect>                
                                <div 
                                    class="invalid-feedback"
                                    :class="{'d-block': v$.step[0].woundDetails.$invalid}"
                                >
                                    Invalid feedback 
                                </div>

                                <div v-if="step[0].woundDetails && step[0].woundDetails.indexOf('Abbey Pain Assessment') !== -1" class="mt-3">
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="woundDetailsAbbeyPainAssessment"
                                        placeholder="Abbey Pain Assessment Score"
                                        v-model="step[0].woundDetailsAbbeyPainAssessment"
                                        :class="{
                                            'is-invalid': v$.step[0].woundDetailsAbbeyPainAssessment.$invalid,
                                        }"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].woundDetailsAbbeyPainAssessment.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>

                                <div v-if="step[0].woundDetails && step[0].woundDetails.indexOf('Swabs Taken') !== -1" class="mt-3">
                                    <input
                                        type="text"
                                        class="form-control"
                                        name="woundDetailsSwabsTaken"
                                        placeholder="Swabs Taken Details"
                                        v-model="step[0].woundDetailsSwabsTaken"
                                        :class="{
                                            'is-invalid': v$.step[0].woundDetailsSwabsTaken.$invalid,
                                        }"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        :class="{
                                            'd-block': v$.step[0].woundDetailsSwabsTaken.$invalid,
                                        }"
                                    >
                                        Invalid feedback
                                    </div>
                                </div>

                            </div>

                            <div class="mb-3">
                                <div class="form-label">Notes</div>
                                <textarea
                                    rows="4"
                                    class="form-control"
                                    placeholder="Notes"
                                    value=""
                                    spellcheck="false"
                                    name="notes"
                                    v-model="step[0].notes"
                                    :class="{
                                        'is-invalid': v$.step[0].notes.$invalid,
                                    }"
                                ></textarea>
                                <div class="invalid-feedback" :class="{'d-block': v$.step[0].notes.$invalid}">Invalid feedback</div>
                            </div>
                            
                            <div class="mb-3">
                                <div class="form-label">Signature</div>                     
                                <input
                                    type="text"
                                    class="form-control w-75 mt-2"
                                    name="observationSignature"
                                    placeholder="Enter your full name"
                                    v-model="step[0].observationSignature"
                                    :class="{'is-invalid': v$.step[0].observationSignature.$invalid}"
                                />
                                <div
                                    class="invalid-feedback"
                                    :class="{
                                        'd-block': v$.step[0].observationSignature.$invalid,
                                    }"
                                >
                                    Invalid feedback
                                </div>
                            </div>      

                        </div>
                    </div>

                </div>
                
                <div class="card-footer justify-content-end">                    
                    <p class="d-flex align-items-center mb-0">
                        
                        <a :href="'/care-plans/' + carePlanID" class="p-2 mx-3 d-block back-btn" id="last-back-btn">Cancel</a>
                        <a @click="onClickNext()" class="ml-5 btn btn-primary" id="submit-btn">Finish</a>
                    </p>
                </div>
                
            </div>

        </form>
    </div>
</template>

<script>
import {alpha, alphaNum, email, integer, required, requiredIf, minLength, maxLength, minValue, maxValue, helpers} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import Multiselect from 'vue-multiselect';
import moment from 'moment';

// Custom Validators
const inArray = (array) => (value) => array.includes(value) || !helpers.req(value);
const isDateFormat = (value) => value.match("^$|((?:[0-9]{2})?[0-9]{2})-(1[0-2]|0?[1-9])-(3[01]|[12][0-9]|0?[1-9])") || !helpers.req(value);
const isTimeFormat = (value) => value.match("^$|^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$") || !helpers.req(value);
const isTextWithSpace = (value) => value.match("^[a-zA-Z0-9_ \s\'\"]*$") || !helpers.req(value);

export default {
    name: "woundCarePlanMonitorComponent",
    props: ["progressBar", "pageTitle", "residentId", "userId", "carePlan", "operation"], // vue can read kebab props as camel cased vars
    components: { Multiselect },
    setup() {
        return {v$: useVuelidate()};
    },
    data() {             
        var carePlanObject = JSON.parse(this.carePlan); 
        var endpointToUse = location.protocol + "//" + window.location.host + "/api/care-plans/" + carePlanObject.id + '/' + process.env.MIX_API_KEY; 
        
        carePlanObject['wound_care_plan'] = JSON.parse(carePlanObject['wound_care_plan']);

        // Returs empty string when object doesn't exist 
        // options - array | string
        function returnValueFromObject(fieldName, defaultValue = "", options) {      
            // Editing
            if (!_.isEmpty(carePlanObject)) {
                return carePlanObject['wound_care_plan'][fieldName];
            } else {
                return defaultValue;
            }
        }

        return {
            currentStep: 1, 
            maxStep: 1,
            percentageComplete: 0,
            errors: 0,
            trueFalseOptions: [true,false],
            stageOfTheWoundOptions: ['Dry/Scab', 'Necrotic/Black', 'Sloughy/Yellow', 'Granulating/Red','Epithelialising /Pink' ],
            stageOfSurroundingSkinOptions: ['Healthy/intact', 'Macerated/Moist', 'Reddened', 'Swollen','Hot', 'Fragile/Thin', 'Dry/Scaly', 'Pigmented', 'Weeping', 'Pale/Shiny'],
            exudateOptions: ['Haemo-serous', 'Serous', 'Purulent'],
            amountOfExudateOptions: ['Scant/small', 'Moderate', 'Large'],
            woundDetailsOptions: ['Odour', 'Pain Present', 'Wound Infected', 'Swabs Taken', 'On Antibiotics', 'Infection Control Form', 'Accident/Incident Form Completed', 'Abbey Pain Assessment'],           
            endpoint: endpointToUse,
            carePlanID: JSON.parse(this.carePlan)['id'],
            carePlanStatus: JSON.parse(this.carePlan)['status'],
            step: [
                // Step 1 - Observation
                {
                    sizeOfWound : '',
                    stageOfTheWound : '',
                    stageOfSurroundingSkin : '',
                    exudate : '',
                    amountOfExudate : '',
                    woundDetails : '',
                    woundDetailsAbbeyPainAssessment : '',
                    woundDetailsSwabsTaken : '',
                    notes : '',
                    observationSignature : ''             
                },
            ],
        };
    },
    validations() {
        const dateType = {
            required, 
            isDateFormat, 
            $lazy: true, 
            $autoDirty: true
        };  
             
        const timeType = {
            required, 
            isTimeFormat, 
            $lazy: true, 
            $autoDirty: true
        };

        // TEXT TYPES: default: 191, mid:500, long:1000
        function text(minimum = 3,maximum = 191, isRequired = false) {
            var rule = {
                required,
                isTextWithSpace,
                minLength: minLength(minimum),
                maxLength: maxLength(maximum),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) { // if not required, remove the 'required' rule
                rule = Object.assign({}, rule);
                delete rule['required']
            }

            return rule;
            
        };        

        // ARRAY TYPES        
        function inOptions(options, isRequired = false) {
            var rule = {
                required,
                inOptions: inArray(options),
                $lazy: true,
                $autoDirty: true,
            };

            if (!isRequired) {
                rule = Object.assign({}, rule);
                delete rule['required']
            }
            return rule;
        };
        
        // Optionally add this on types that doesn't have the 'required' parameter
        function notRequired(type) {
            var validations = Object.assign({}, type);                
            delete validations['required'];
            
            return validations;
        };

        return {
            step: [
                // Step 1 - Observation
                {   
                    sizeOfWound : text(3,191, true),
                    stageOfTheWound : inOptions(this.stageOfTheWoundOptions),
                    stageOfSurroundingSkin : {      
                        // $each: helpers.forEach({ //TODO: figure out why we can't validate each entry
                        //     minLength: minLength(3),
                        //     required
                        // }),
                        $lazy: true,
                        $autoDirty: true,
                    },
                    exudate : inOptions(this.exudateOptions),
                    amountOfExudate : inOptions(this.amountOfExudateOptions),
                    woundDetails : {      
                        // $each: helpers.forEach({ //TODO: figure out why we can't validate each entry
                        //     minLength: minLength(3),
                        //     required
                        // }),
                        $lazy: true,
                        $autoDirty: true,
                    },
                    woundDetailsAbbeyPainAssessment : text(),
                    woundDetailsSwabsTaken : text(),
                    notes :  text(3,500, true),
                    observationSignature :  text(3,191, true),
                },
            ],
        };
    },

    computed: {},

    methods: {
        onClickNext: function () {        
            
            // Validate fields on current step
            this.v$.step[this.currentStep - 1].$touch();

            // if there are no errors in the current step
            if (!this.v$.step[this.currentStep - 1].$error) {

                // All steps
                if (this.currentStep < this.maxStep) {                           
                    // Proceed to next step
                    this.currentStep++;

                // Final step
                } else if (this.currentStep == this.maxStep) {
                    
                    // Initialize alerts and toast notifications for displaying message  
                    const toastError = Swal.mixin({          
                            toast: true,                
                            position: 'top-end', 
                            showConfirmButton: true, 
                        });  
                    const alertSuccess = Swal.mixin({                          
                            position: 'center', 
                            showConfirmButton: true, 
                            timer: 3000,
                            timerProgressBar: true,
                        });  

                    // Show button spinner
                    document.getElementById('submit-btn').innerHTML  = '<i class="fa fa-circle-notch fa-spin"></i>';
                    document.getElementById('submit-btn').disabled  = true;
                    document.getElementById('last-back-btn').setAttribute('style', 'display:none !important');
   
                    var formData = {};
                    
                    // Loop through all steps
                    for(let i=0; i < this.maxStep; i++){ 
                        // Loop through all the step fields
                        for ( const [key,value] of Object.entries( this.step[i] ) ) {   
                            formData[key] = value;                     
                        }
                    }

                    // Add residentID, userID, and type fields
                    formData['residentID'] = this.residentId;
                    formData['userID'] = this.userId;
                    formData['type'] = "Wound";             
                    formData['updateType'] = 'monitor';
                    
                    this.updateCarePlan(formData, toastError, alertSuccess);
                   
                }
                this.percentageComplete = Math.round((this.currentStep / this.maxStep) * 100);
            }
        },

        updateCarePlan: function(formData, toastError, alertSuccess) {           
            // Call the update endpoint
            axios.put(this.endpoint, formData)
                .then((response) => {
                    if(response.data.error == 403){                                
                        console.log('Bad Request');
                        console.log(response.data);
                        
                        // Show bad request toast message. This error occurs when the input fields are not in the expected format
                        toastError.fire({ 
                            type: 'warning', 
                            title: 'The input fields are malformed and cant be processed.' 
                        }); 
                    } else {
                        if(response.data.id == null){
                            console.log('Validation Error');
                            console.log(response.data);
                            
                            // Show validation toast message. This error only occurs when the javascript validation is bypassed and the API endpoint returned validation error instead of the resident object
                            toastError.fire({ 
                                type: 'warning', 
                                title: 'There are validation errors found on the form.'
                            });                             
                        }
                        else{
                            console.log("Success");
                            console.log(response.data);

                            // Show success alert message and redirect to the index page. This occurs when the form submission is successful and the resident object was updated
                            this.formSuccess = true;
                            window.residentId = this.residentId;

                            alertSuccess.fire({
                                title: "Success!",
                                text: "The wound observation has been added.",
                                icon: "success",                           
                            }).then(function(){
                                window.location = "/care-plans/"+ response.data.id;
                            }); 
                        }
                    }                                        
                    
                })
                .catch((err) => {                            
                    console.log("Endpoint Error!");
                    console.log(err.response);
                    toastError.fire({ type: 'error', title: 'Sorry, request failed to submit.' }); // this error occurs when endpoint is wrong
                })
        }        
        
    },
};
</script>
